import { refLabelIn } from '@versesverse/schema/numbering';
import { VerseTranslation } from './VerseTranslation';
import { useLang } from '../i18n/lang';
import { useGrowOnScroll } from '../reports/useGrowOnScroll';
import type { Passage } from './passages';
import { PassageText } from './PassageText';

/*
 * THE PASSAGES THEMSELVES, rendered.
 *
 * Pure and dumb: it takes the assembled passages and the load status and draws them — the
 * citation, the English, the original beside it with its own language and direction, and the one
 * link out to read it elsewhere. A passage whose range gave up shows the citation and the way out,
 * not a blank, so "could not load this" never looks like "this does not exist".
 */
export interface VersesListProps {
  status: 'idle' | 'loading' | 'ready';
  passages: Passage[];
  incomplete: boolean;
  /** How many passages are coming, where that is known before their text is — see `useNodeVerses`. */
  expected?: number;
  /** Travel to another name the verse text mentions — the map's own exploration loop. */
  onJump?: (id: string) => void;
  /** Open a verse into the passage around it (the expanded reader). Makes the citation a control. */
  onExpand?: (id: number) => void;
  /** Open a discussion for a single verse, by its citation. Absent → no per-verse discuss control. */
  onDiscuss?: (ref: string) => void;
  /**
   * WHICH SCRIPTURE THE ORDER STARTS FROM, already resolved to what a reader calls it.
   *
   * Null means no lead was chosen and the rotation starts wherever the passages do. The list does
   * not sort — `sortPassages` did that before these arrived — it only says what was done.
   */
  lead?: string | null;
  /** The verse to mark as the one being read around, when this list is the expanded passage. */
  highlightId?: number;
}

/**
 * How many passages are drawn before the reader has scrolled anywhere.
 *
 * Twenty, as the reports beside them use. Their panel pages three at a time behind a control; this
 * is a screenful, and reaching the end asks for the next one without being told to.
 */
const VERSES_STEP = 20;

/**
 * WHERE THE VERSES BEGIN, so something above can send a reader down to them.
 *
 * Exported rather than spelled twice: a jump and its target that write the same string in two files
 * are a pair nothing keeps in step.
 */
export const PASSAGES_ID = 'panel-passages';

export function VersesList({ status, passages, incomplete, expected = 0, onJump, onExpand, onDiscuss, highlightId, lead = null }: VersesListProps): React.JSX.Element | null {
  const { lang, t } = useLang();
  /* Before the early returns: a hook called conditionally is called in a different order on the
     next render, and React has no way to match it up. */
  const { shown, sentinel } = useGrowOnScroll(passages.length, VERSES_STEP);

  if (status === 'idle') return null;
  if (status === 'loading') {
    /*
     * HOW MANY ARE COMING, WHERE THAT IS KNOWN — see `useNodeVerses.expected`.
     *
     * The ids arrive from `refs/*.json` long before the ranges holding their text: measured, ~343ms
     * against 4.1 to 5.4 seconds for the first passage. A reader watched one unchanging line for all
     * of it, with no way to tell a figure with 387 passages from a request that had stalled.
     *
     * MORE THAN ONE, not more than zero. Zero means not yet known rather than none, so the plain
     * sentence stands while the ids are in flight; and one would read "Opening 1 passages", which is
     * a number bought at the price of the grammar. A single passage is not a wait worth explaining.
     *
     * A count of 0 while
     * the ids are still in flight would be a number that is wrong for a moment, which is worse than
     * no number at all.
     */
    return (
      <p className="verses-loading" role="status" aria-live="polite">
        {expected > 1 ? t('verses.loadingCount', { count: expected }) : t('verses.loading')}
      </p>
    );
  }

  /* Whether there is more than one scripture here to alternate between — see the note below. */
  const woven = new Set(passages.map(p => p.corpusId)).size > 1;

  return (
    <div className="verses">
      <h3 className="panel-heading" id={PASSAGES_ID}>{t('verses.heading')}</h3>
      {/*
        * WHY THEY ARE IN THIS ORDER. Theirs says it too, in the same place: an order a reader cannot
        * account for reads as no order at all, and this one is the point — the scriptures alternate
        * so that a figure's traditions stand beside each other rather than in blocks.
        *
        * Only where there is more than one scripture to alternate BETWEEN. On the rest of the roster
        * it would be a sentence about nothing.
        */}
      {woven && (
        <p className="verses-order">
          {lead === null ? t('verses.orderEach') : t('verses.orderFrom', { text: lead })}
        </p>
      )}
      <ol className="verses-list">
        {passages.slice(0, shown).map(passage => {
          const classes = passage.id === highlightId ? 'verse verse--center' : 'verse';
          /* The WHOLE verse opens the reader, not only its citation — but a click on one of the verse's
             own controls (a name that jumps, the source link, discuss, the auto-translation) does its
             own thing, and a click that is really the end of a text selection is left alone, so a reader
             can still select and copy the words. The citation stays a real button, so keyboard and
             screen-reader users have the same door. */
          const openFromCard = onExpand
            ? (event: React.MouseEvent<HTMLLIElement>): void => {
                const target = event.target;
                if (target instanceof HTMLElement && target.closest('a, button')) return;
                if (window.getSelection()?.toString()) return;
                onExpand(passage.id);
              }
            : undefined;
          return (
          <li
            key={passage.id}
            className={openFromCard ? `${classes} verse--openable` : classes}
            onClick={openFromCard}
          >
            <cite className="verse-ref">
              {onExpand
                ? <button type="button" className="verse-ref-open" onClick={() => onExpand(passage.id)}>{passage.ref}</button>
                : passage.ref}
              {/*
                * WHAT THE PASSAGE IS CALLED, where its reference does not say.
                *
                * "Isaiah 53" names its book; "Quran 2" does not name Al-Baqara, and the library has
                * held all 114 surah names the whole time. Printed verbatim — "Surah 2 Al-Baqara"
                * repeats the reference beside it and is tempting to shorten, and a rule for
                * abbreviating somebody's name for a chapter of their scripture is not one a verse
                * list gets to invent.
                */}
              {passage.section && <span className="verse-section">{passage.section}</span>}
              {/*
                * WHAT A TANAKH CALLS THIS VERSE, where the two traditions disagree.
                *
                * The reference on this page is King James numbering, which is a convention and not
                * a neutral fact — a reader looking up Joel 3:1 in a Hebrew Bible lands on this
                * page's Joel 2:28. `lib/versification.ts` has always repaired the PAIRING, so the
                * Hebrew shown is the right Hebrew; what was missing is the other NUMBER.
                *
                * No control and no preference, on purpose. It appears only on the ~35 chapters
                * where the conventions differ, so it is information where information exists
                * rather than a setting a reader must find and understand first. Where they agree,
                * `refLabelIn` returns nothing and nothing is drawn.
                */}
              {refLabelIn('hebrew', passage.ref) && (
                <span className="verse-ref-alt" title={t('verses.hebrewNumberingWhy')}>
                  {' · '}{t('verses.hebrewNumbering')} {refLabelIn('hebrew', passage.ref)}
                </span>
              )}
            </cite>
            <PassageText passage={passage} onJump={onJump} />
            {/* Which collection a hadith is from, and what the tradition grades it — the two facts
                that decide what weight a report carries, and the hadith were shown without either. */}
            {passage.provenance && <p className="source-line source-muted">{passage.provenance}</p>}
            {passage.original && (
              <p className="verse-original" lang={passage.originalLang} dir={passage.originalRtl ? 'rtl' : undefined}>
                {passage.original}
              </p>
            )}
            {/* AFTER every authoritative reading (both translations and the original), so the opt-in
                machine reading is plainly a separate action — never mistaken for one of the editions.
                Skipped where the reader's language already IS the passage's original. */}
            {passage.text && lang !== passage.originalLang && (
              <VerseTranslation text={passage.text} />
            )}
            {/* WHAT THE MANUSCRIPTS SAY, beside the edition and licence because it is the same kind
                of fact: whose text this is and how it got here. The King James prints the long
                ending of Mark, the heavenly witnesses and the woman taken in adultery, and marks
                none of them — so without this a reader meets 1 John 5:7 with nothing on the page. */}
            {/* A VERSE ITS OWN EDITION DOES NOT HAVE SAYS SO, rather than looking badly built.
                Nestle 1904 has no Matthew 17:21 — the earliest manuscripts lack it — and shown in
                English alone that reads as a formatting fault instead of one of the most
                consequential facts about the New Testament text. Names the edition and claims no
                cause: 182 passages lack an original and the reasons differ. */}
            {passage.originalAbsent && (
              <p className="verse-textual" dir="auto">
                {t('verses.noOriginal', { edition: passage.originalAbsent })}
              </p>
            )}
            {passage.textualNote && (
              <p className="verse-textual" dir="auto">{passage.textualNote}</p>
            )}
            {/* WHAT THE SOURCE SAYS ABOUT THIS REPORT'S STANDING.
                A hadith is not only a text: HadeethEnc grades every one of its 2,328 reports and
                names who narrated it, and whether a report is graded authentic is exactly what a
                library built to show every side must not flatten. Joined rather than written into
                a sentence — the source made two statements and merging them would put a claim in
                its mouth. Verbatim: 219 attributions disagree with themselves and normalising them
                would be an editorial act. */}
            {(passage.note?.attribution ?? passage.note?.grade) && (
              <p className="verse-stated" dir="auto">
                {[passage.note?.attribution, passage.note?.grade].filter(Boolean).join(' · ')}
              </p>
            )}
            {passage.edition && (
              <p className="verse-edition">
                <span className="verse-edition-title">{passage.edition.title}</span>
                {passage.edition.licence && <span className="verse-edition-licence"> · {passage.edition.licence}</span>}
                {passage.edition.nonCommercial && (
                  <span className="verse-edition-nc" title={t('verses.nonCommercialTitle')}> · {t('verses.nonCommercial')}</span>
                )}
              </p>
            )}
            {passage.source && passage.sourceName && (
              <a className="verse-source" href={passage.source} target="_blank" rel="noopener noreferrer">
                {t('verses.readOn', { source: passage.sourceName })} ↗
              </a>
            )}
            {onDiscuss && (
              <button type="button" className="verse-discuss" onClick={() => onDiscuss(passage.ref)}>
                {t('verses.discuss')}
              </button>
            )}
          </li>
          );
        })}
      </ol>
      {/*
        * MORE AS THE READER REACHES THE END — the same sentinel the reports have had all along.
        *
        * Children of Israel has 2,653 passages and the panel rendered every one: each a citation,
        * an English verse, an original in Hebrew or Arabic, a source line and a link out. The panel
        * came out 951,204 pixels tall, which is a scrollbar thumb about a pixel high and tens of
        * thousands of nodes built before the reader has read a word.
        *
        * `useGrowOnScroll` was written for exactly this and used on the reports beside it — Abu
        * Hurayra's 1,825, Muhammad's 2,600 — and the passages, the longer list of the two, never got
        * it. Theirs pages three at a time behind a control; scrolling IS the request, so there is no
        * control here.
        */}
      <div ref={sentinel} aria-hidden="true" />
      {incomplete && (
        <p className="verses-incomplete" role="status">{t('verses.incomplete')}</p>
      )}
    </div>
  );
}
